import React from "react";
import { Link } from "react-router-dom";
import { Contact, ContactType } from "@akfdev-com/shared";
import { toGbpDisplay } from "./gbp";

interface ContactGridViewProps {
  contacts: Contact[];
}

const typeColors: Record<
  ContactType,
  { bg: string; text: string; stripHue: string }
> = {
  resident: { bg: "#ede9fe", text: "#6d28d9", stripHue: "#8b5cf6" },
  supplier: { bg: "#fef3c7", text: "#92400e", stripHue: "#f59e0b" },
  customer: { bg: "#dbeafe", text: "#1e40af", stripHue: "#3b82f6" },
};

/**
 * Contact Grid View
 *
 * Renders contacts as 360×168 cards in a 3-column grid (18px gaps, optimal at 1180px width).
 * Each card has:
 * - Type-hued top strip (purple/amber/blue)
 * - Type badge
 * - Name
 * - Email + phone (if present)
 * - Owed (green) and Owing (red) amounts
 *
 * All amounts displayed via toGbpDisplay with semantic colours.
 *
 * Validates: Requirements 8.1, 8.5
 */
export const ContactGridView: React.FC<ContactGridViewProps> = ({
  contacts,
}) => {
  return (
    <div
      className="grid gap-4"
      style={{
        gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
        gap: "18px",
      }}
    >
      {contacts.map((contact) => {
        const typeInfo = typeColors[contact.type];

        return (
          <Link
            key={contact.id}
            to={`/dashboard/contacts/${contact.id}`}
            className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden flex flex-col"
            style={{ width: "360px", height: "168px" }}
          >
            {/* Type-hued top strip */}
            <div
              style={{
                backgroundColor: typeInfo.stripHue,
                height: "8px",
              }}
            />

            {/* Card content */}
            <div className="p-4 flex flex-col justify-between flex-1">
              {/* Top section: type badge + name */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: typeInfo.bg,
                      color: typeInfo.text,
                    }}
                  >
                    {contact.type.charAt(0).toUpperCase() +
                      contact.type.slice(1)}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {contact.name}
                </h3>
              </div>

              {/* Middle section: email + phone */}
              <div className="text-xs text-gray-500 space-y-0.5">
                {contact.email && <p className="truncate">{contact.email}</p>}
                {contact.phone && <p className="truncate">{contact.phone}</p>}
              </div>

              {/* Bottom section: owed + owing */}
              <div className="flex gap-4 text-right">
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Owed</p>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "#16a34a" }}
                  >
                    £{toGbpDisplay(contact.totalOwed || 0)}
                  </p>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Owing</p>
                  <p
                    className="text-sm font-semibold"
                    style={{ color: "#dc2626" }}
                  >
                    £{toGbpDisplay(contact.totalOwing || 0)}
                  </p>
                </div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};
